import posthog from "posthog-js"

export type SearchPostHogPrimitive = string | number | boolean | null | undefined
export type SearchPostHogValue = SearchPostHogPrimitive | SearchPostHogValue[] | { [key: string]: SearchPostHogValue }
export type SearchPostHogProperties = Record<string, SearchPostHogValue>

interface SearchPostHogConfig {
	enabled: boolean
	apiKey: string
	host: string
	captureQueryText: boolean
}

interface SearchCorrelationInput {
	organizationId?: unknown
	externalSessionId?: unknown
	storeSlug?: unknown
	storeUrl?: unknown
}

interface SearchProductInput {
	id?: unknown
	category?: unknown
	brand?: unknown
	price?: unknown
	currency?: unknown
	inStock?: unknown
	url?: unknown
}

const DEFAULT_DISTINCT_ID = "search-standalone"
const DEFAULT_HOST = "https://us.i.posthog.com"

function readBooleanEnv(name: keyof ImportMetaEnv, defaultValue: boolean): boolean {
	const raw = import.meta.env[name]
	if (raw === undefined) {
		return defaultValue
	}
	return String(raw).trim().toLowerCase() === "true"
}

function normalizeString(value: unknown): string | undefined {
	if (typeof value !== "string") {
		return undefined
	}
	const trimmed = value.trim()
	return trimmed.length > 0 ? trimmed : undefined
}

function toSafeUrlHost(url: unknown): string | undefined {
	const value = normalizeString(url)
	if (!value) {
		return undefined
	}

	try {
		const parsed = new URL(value.startsWith("http") ? value : `https://${value}`)
		return parsed.hostname.replace(/^www\./, "")
	} catch {
		return undefined
	}
}

function sanitizeError(error: unknown): SearchPostHogProperties {
	if (error instanceof Error) {
		return {
			error_name: error.name,
			error_message: error.message,
		}
	}

	return {
		error_name: "NonError",
		error_message: typeof error === "string" ? error : "Unknown error",
	}
}

/**
 * Standalone-search-specific PostHog browser wrapper.
 *
 * This intentionally does not use the extension CustomPostHogProvider because
 * standalone search has separate runtime, deploy, and privacy semantics.
 */
export class SearchPostHogClient {
	private static instance: SearchPostHogClient

	private readonly config: SearchPostHogConfig
	private initialized = false

	private constructor() {
		this.config = {
			enabled: readBooleanEnv("VITE_SEARCH_POSTHOG_ENABLED", false),
			apiKey: (import.meta.env.VITE_SEARCH_POSTHOG_API_KEY || "").trim(),
			host: (import.meta.env.VITE_SEARCH_POSTHOG_HOST || DEFAULT_HOST).trim() || DEFAULT_HOST,
			captureQueryText: readBooleanEnv("VITE_SEARCH_POSTHOG_CAPTURE_QUERY_TEXT", true),
		}
	}

	static getInstance(): SearchPostHogClient {
		if (!SearchPostHogClient.instance) {
			SearchPostHogClient.instance = new SearchPostHogClient()
		}
		return SearchPostHogClient.instance
	}

	initialize(): void {
		if (this.initialized || !this.config.enabled) {
			return
		}

		if (!this.config.apiKey) {
			console.warn("[SearchPostHogClient] VITE_SEARCH_POSTHOG_ENABLED=true but VITE_SEARCH_POSTHOG_API_KEY is not set")
			return
		}

		try {
			posthog.init(this.config.apiKey, {
				api_host: this.config.host,
				disable_session_recording: true,
				capture_pageview: false,
				capture_dead_clicks: false,
				autocapture: false,
				advanced_disable_decide: true,
			})
			this.initialized = true
		} catch (error) {
			console.warn("[SearchPostHogClient] Failed to initialize PostHog:", error)
		}
	}

	isEnabled(): boolean {
		return this.initialized
	}

	capture(event: string, properties: SearchPostHogProperties = {}, distinctId?: string): void {
		if (!this.isEnabled()) {
			return
		}

		try {
			const safeProperties = this.withDefaultProperties(properties)
			posthog.capture(event, {
				...safeProperties,
				distinct_id: distinctId || this.getDistinctIdFromProperties(safeProperties),
				...this.getGroupsFromProperties(safeProperties),
			})
		} catch (error) {
			console.warn(`[SearchPostHogClient] Failed to capture '${event}':`, error)
		}
	}

	identify(distinctId: string, properties: SearchPostHogProperties = {}): void {
		if (!this.isEnabled() || !distinctId.trim()) {
			return
		}

		try {
			posthog.identify(distinctId, this.withDefaultProperties(properties))
		} catch (error) {
			console.warn(`[SearchPostHogClient] Failed to identify '${distinctId}':`, error)
		}
	}

	reset(): void {
		if (!this.isEnabled()) {
			return
		}

		try {
			posthog.reset()
		} catch (error) {
			console.warn("[SearchPostHogClient] Failed to reset PostHog:", error)
		}
	}

	buildQueryProperties(query: unknown): SearchPostHogProperties {
		const queryText = typeof query === "string" ? query : query == null ? "" : String(query)
		const trimmed = queryText.trim()
		const properties: SearchPostHogProperties = {
			query_length: queryText.length,
			query_token_count: trimmed ? trimmed.split(/\s+/).length : 0,
			query_is_empty: trimmed.length === 0,
		}

		if (this.config.captureQueryText) {
			properties.query_text = queryText
		}

		return properties
	}

	safeStoreProperties(input: { storeUrl?: unknown; storeSlug?: unknown }): SearchPostHogProperties {
		return {
			...(normalizeString(input.storeSlug) ? { store_slug: normalizeString(input.storeSlug) } : {}),
			...(toSafeUrlHost(input.storeUrl) ? { store_url_host: toSafeUrlHost(input.storeUrl) } : {}),
		}
	}

	normalizeError(error: unknown): SearchPostHogProperties {
		return sanitizeError(error)
	}

	buildCorrelationProperties(input: SearchCorrelationInput): SearchPostHogProperties {
		return {
			...(normalizeString(input.organizationId) ? { organization_id: normalizeString(input.organizationId) } : {}),
			...(normalizeString(input.externalSessionId)
				? { external_session_id: normalizeString(input.externalSessionId) }
				: {}),
			...this.safeStoreProperties({ storeSlug: input.storeSlug, storeUrl: input.storeUrl }),
		}
	}

	buildSafeProductProperties(product: SearchProductInput): SearchPostHogProperties {
		return {
			...(normalizeString(product.id) ? { product_id: normalizeString(product.id) } : {}),
			...(normalizeString(product.category) ? { product_category: normalizeString(product.category) } : {}),
			...(normalizeString(product.brand) ? { product_brand: normalizeString(product.brand) } : {}),
			...(typeof product.price === "number" && Number.isFinite(product.price) ? { product_price: product.price } : {}),
			...(normalizeString(product.currency) ? { product_currency: normalizeString(product.currency) } : {}),
			...(typeof product.inStock === "boolean" ? { product_in_stock: product.inStock } : {}),
			product_has_url: Boolean(normalizeString(product.url)),
		}
	}

	private withDefaultProperties(properties: SearchPostHogProperties): SearchPostHogProperties {
		return {
			...properties,
			service: "search-standalone",
			runtime: "browser",
			posthog_integration: "search-service",
		}
	}

	private getDistinctIdFromProperties(properties: SearchPostHogProperties): string {
		const externalSessionId = properties.external_session_id
		if (typeof externalSessionId === "string" && externalSessionId.trim()) {
			return externalSessionId
		}

		const organizationId = properties.organization_id
		if (typeof organizationId === "string" && organizationId.trim()) {
			return organizationId
		}

		return DEFAULT_DISTINCT_ID
	}

	private getGroupsFromProperties(properties: SearchPostHogProperties): SearchPostHogProperties {
		const organizationId = properties.organization_id
		if (typeof organizationId === "string" && organizationId.trim()) {
			return { $groups: { organization: organizationId } }
		}

		return {}
	}
}
